import React, { useRef } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';

export const TEAM_COLORS = {
  PM1: '#e11d48', PM2: '#2563eb', PM3: '#16a34a', PM4: '#d97706', PM5: '#7c3aed',
  PM6: '#0891b2', PM7: '#db2777', PM8: '#4d7c0f', PM9: '#b91c1c', PM10: '#0d9488',
};
export const colorForTeam = (t) => TEAM_COLORS[t] || '#64748b';
export const pad = (n) => String(n).padStart(2, '0');

const WEEKDAYS_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const WEEKDAYS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const LONG_PRESS_MS = 2000;

const DayCell = ({ team, dateKey, d, jobs, isToday, color, pt, onDayClick, block, note, onLongPress }) => {
  const realJobs = jobs.filter((j) => !j._ghost);
  const ghosts = jobs.filter((j) => j._ghost);
  const hasJobs = realJobs.length > 0;
  const { listeners, attributes, setNodeRef, isDragging } = useDraggable({
    id: `day|${team}|${dateKey}`,
    data: { team, dateKey },
    disabled: !hasJobs,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `daycell|${team}|${dateKey}` });
  const setRefs = (el) => { setNodeRef(el); setDropRef(el); };
  const timerRef = useRef(null);
  const posRef = useRef(null);
  const firedRef = useRef(false);

  const startPress = (x, y) => {
    posRef.current = { x, y };
    firedRef.current = false;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      firedRef.current = true;
      onLongPress(team, dateKey, posRef.current.x, posRef.current.y);
    }, LONG_PRESS_MS);
  };
  const movePress = (x, y) => {
    if (posRef.current && (Math.abs(x - posRef.current.x) > 12 || Math.abs(y - posRef.current.y) > 12)) {
      clearTimeout(timerRef.current);
    }
  };
  const endPress = () => clearTimeout(timerRef.current);

  const totalWindows = realJobs.reduce((s, j) => s + (j.windows || 0), 0);
  const totalDoors = realJobs.reduce((s, j) => s + (j.doors || 0), 0);
  const hasService = realJobs.some((j) => j.job_type === 'SERVICE');
  const hasProv = realJobs.some((j) => j._prov);
  const ghostWindows = ghosts.reduce((s, j) => s + (j.windows || 0), 0);
  const ghostDoors = ghosts.reduce((s, j) => s + (j.doors || 0), 0);
  const ghostTo = [...new Set(ghosts.map((j) => {
    const toT = j._movedTo === 'ANY' ? '?' : j._movedTo;
    const toD = j._movedToDate ? parseInt(j._movedToDate.split('-')[2], 10) : null;
    if (!toT) return null;
    if (toD && toT === team) return `${pt ? 'dia' : 'day'} ${toD}`;
    return toD ? `${toT}·${toD}` : toT;
  }).filter(Boolean))].join(',');

  let borderStyle = {};
  if (isOver && !isDragging) borderStyle = { boxShadow: 'inset 0 0 0 2px #10b981', background: '#ecfdf5' };
  else if (block) borderStyle = { boxShadow: 'inset 0 0 0 2px #dc2626', background: '#fef2f2' };
  else if (hasProv) borderStyle = { boxShadow: 'inset 0 0 0 2px #f59e0b', background: '#fffbeb' };
  else if (hasJobs && !isToday) borderStyle = { background: `${color}14` };
  else if (!hasJobs && ghosts.length > 0) borderStyle = { background: 'repeating-linear-gradient(45deg, rgba(100,116,139,0.10) 0 4px, transparent 4px 8px)' };

  return (
    <button
      ref={setRefs}
      {...listeners}
      {...attributes}
      type="button"
      data-calendar-day="true"
      data-testid={`compare-day-${team}-${dateKey}`}
      onClick={(e) => { if (firedRef.current) return; if (hasJobs) onDayClick(realJobs, e); }}
      onContextMenu={(e) => { e.preventDefault(); onLongPress(team, dateKey, e.clientX, e.clientY); }}
      onPointerDown={(e) => startPress(e.clientX, e.clientY)}
      onPointerMove={(e) => movePress(e.clientX, e.clientY)}
      onPointerUp={endPress}
      onPointerCancel={endPress}
      onPointerLeave={endPress}
      className={`min-h-[54px] border-b border-r p-0.5 flex flex-col items-center justify-center transition-colors relative ${isToday ? 'bg-blue-50' : ''} ${hasJobs ? 'cursor-grab active:cursor-grabbing hover:bg-slate-50' : 'cursor-default'} ${isDragging ? 'opacity-40' : ''}`}
      style={{ touchAction: 'manipulation', ...borderStyle }}
    >
      <span className={`text-[11px] leading-none font-medium ${isToday ? 'text-blue-600 font-bold' : hasJobs ? 'text-slate-700' : 'text-slate-400'}`}>{d}</span>
      {hasJobs && (
        <div className="flex flex-wrap justify-center gap-x-1 text-[9px] mt-0.5 font-semibold">
          {hasService && <span className="text-emerald-600">S</span>}
          {totalDoors > 0 && <span>🚪{totalDoors}</span>}
          {totalWindows > 0 && <span>🪟{totalWindows}</span>}
        </div>
      )}
      {ghosts.length > 0 && (
        <div className="flex flex-wrap justify-center items-center gap-x-0.5 text-[8px] mt-0.5 font-semibold text-slate-400 opacity-70 line-through" data-testid={`compare-ghost-${team}-${dateKey}`}>
          {ghostDoors > 0 && <span>🚪{ghostDoors}</span>}
          {ghostWindows > 0 && <span>🪟{ghostWindows}</span>}
          {ghostDoors === 0 && ghostWindows === 0 && <span>S</span>}
        </div>
      )}
      {ghosts.length > 0 && ghostTo && (
        <span className="text-[7px] leading-none font-bold text-slate-400 mt-0.5 no-underline">→ {ghostTo}</span>
      )}
      {note && (
        <span className="text-[8px] leading-none mt-0.5" title={note.text} data-testid={`compare-note-label-${team}-${dateKey}`}>📝</span>
      )}
      {block && (
        <span className="text-[8px] leading-none font-bold text-red-600 mt-0.5" data-testid={`compare-block-label-${team}-${dateKey}`}>Block</span>
      )}
      {!block && hasProv && (
        <span className="text-[8px] leading-none font-bold text-amber-600 mt-0.5" data-testid={`compare-prov-label-${team}-${dateKey}`}>{pt ? 'Provisório' : 'Provisional'}</span>
      )}
    </button>
  );
};

const CompareTeamCalendar = ({ team, year, month, jobsByDate, todayKey, pt, onDayClick, blocks, notes, onLongPress }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `cal|${team}` });
  const startDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const color = colorForTeam(team);
  const weekdays = pt ? WEEKDAYS_PT : WEEKDAYS_EN;
  return (
    <div
      ref={setNodeRef}
      className={`rounded-xl border bg-white shadow-sm overflow-hidden transition-shadow ${isOver ? 'ring-4 ring-emerald-400 ring-offset-1' : ''}`}
      data-testid={`compare-calendar-${team}`}
    >
      <div className="px-3 py-2 flex items-center gap-2 border-b" style={{ borderTop: `3px solid ${color}` }}>
        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
        <span className="font-bold text-sm text-slate-800">{team}</span>
        {isOver && <span className="text-[10px] font-semibold text-emerald-600 ml-auto">{pt ? 'Soltar aqui para mover' : 'Drop here to move'}</span>}
      </div>
      <div className="grid grid-cols-7 text-center text-[10px] font-semibold text-slate-400 border-b">
        {weekdays.map((w) => <div key={w} className="py-1">{w}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, idx) => {
          if (d === null) return <div key={`e${idx}`} className="min-h-[54px] border-b border-r bg-slate-50/60" />;
          const key = `${year}-${pad(month + 1)}-${pad(d)}`;
          return (
            <DayCell
              key={key}
              team={team}
              dateKey={key}
              d={d}
              jobs={jobsByDate[key] || []}
              isToday={key === todayKey}
              color={color}
              pt={pt}
              onDayClick={onDayClick}
              block={blocks[`${team}|${key}`]}
              note={notes ? notes[`${team}|${key}`] : null}
              onLongPress={onLongPress}
            />
          );
        })}
      </div>
    </div>
  );
};

export const AnyCrewDropZone = ({ pt }) => {
  const { setNodeRef, isOver } = useDroppable({ id: 'cal|ANY' });
  return (
    <div
      ref={setNodeRef}
      className={`fixed top-16 left-1/2 -translate-x-1/2 z-[9999] px-5 py-3 rounded-2xl border-2 border-dashed shadow-xl text-sm font-bold transition-colors ${isOver ? 'bg-slate-700 border-slate-800 text-white scale-105' : 'bg-white border-slate-400 text-slate-600'}`}
      data-testid="any-crew-dropzone"
    >
      ❓ Any other crew — {isOver ? (pt ? 'solte!' : 'drop!') : (pt ? 'solte aqui' : 'drop here')}
    </div>
  );
};

export default CompareTeamCalendar;
